// Role definitions and permissions
export const ROLES = {
  ADMIN: 'admin',
  SALES_REP: 'sales_rep'
};

// Role permissions - what each role can access
export const PERMISSIONS = {
  [ROLES.ADMIN]: {
    // Full access to everything
    dashboard: true,
    sales: {
      view: true,
      create: true,
      edit: true,
      delete: true
    },
    products: {
      view: true,
      create: true,
      edit: true,
      delete: true
    },
    reports: {
      view: true,
      analytics: true
    },
    profile: {
      view: true,
      edit: true
    },
    users: {
      view: true,
      create: true,
      edit: true,
      delete: true,
      assignRoles: true
    }
  },
  [ROLES.SALES_REP]: {
    // Limited access - can only record sales and view personal data
    dashboard: true,
    sales: {
      view: true,
      create: true,
      edit: false, // Can't edit existing sales
      delete: false // Can't delete sales
    },
    products: {
      view: true,
      create: false,
      edit: false,
      delete: false
    },
    reports: {
      view: true,
      analytics: false // Can only view basic reports, not detailed analytics
    },
    profile: {
      view: true,
      edit: true
    },
    users: {
      view: false,
      create: false,
      edit: false,
      delete: false,
      assignRoles: false
    }
  }
};

// Helper function to check if user has permission
export const hasPermission = (userRole, permission) => {
  if (!userRole || !PERMISSIONS[userRole]) {
    return false;
  }
  
  // Handle nested permissions (e.g., 'sales.create')
  const permissionParts = permission.split('.');
  let currentLevel = PERMISSIONS[userRole];
  
  for (const part of permissionParts) {
    if (currentLevel && typeof currentLevel === 'object' && part in currentLevel) {
      currentLevel = currentLevel[part];
    } else {
      return false;
    }
  }
  
  return Boolean(currentLevel);
};

// Helper function to get user role from user object
export const getUserRole = (user) => {
  // Check role property (Firebase user with Firestore data)
  if (user?.role) {
    return user.role;
  }
  
  // Check custom claims (Firebase Auth custom claims)
  if (user?.customClaims?.role) {
    return user.customClaims.role;
  }
  
  // Default to sales_rep if no role is set
  return ROLES.SALES_REP;
};

// Helper function to check if user is admin
export const isAdmin = (user) => {
  return getUserRole(user) === ROLES.ADMIN;
};

// Helper function to check if user is sales rep
export const isSalesRep = (user) => {
  return getUserRole(user) === ROLES.SALES_REP;
};

// Component wrapper for role-based rendering
export const withRoleCheck = (Component, requiredPermission) => {
  return (props) => {
    const { user } = props;
    
    if (!user) {
      return null;
    }
    
    const userRole = getUserRole(user);
    const hasAccess = hasPermission(userRole, requiredPermission);
    
    if (!hasAccess) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to access this page.
            </p>
            <p className="text-sm text-gray-500">
              Contact your administrator for access.
            </p>
          </div>
        </div>
      );
    }
    
    return <Component {...props} />;
  };
};


